import React from "react";
import { 
  FaUndo, 
  FaTruck, 
  FaCalendarAlt, 
  FaExclamationTriangle, 
  FaCreditCard, 
  FaEnvelope, 
  FaFileAlt, 
  FaBoxOpen,
  FaInfoCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaExchangeAlt
} from "react-icons/fa";

export default function RefundPolicy() {
  return (
    <div className="py-12 px-4 md:px-8 bg-gray-50">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 px-6 py-8">
          <h1 className="text-3xl font-bold text-white mb-2">Refund & Cancellation Policy</h1>
          <p className="text-gray-200">
            Last Updated: May 24, 2025
          </p>
        </div>
        
        <div className="p-6 md:p-8">
          {/* Introduction */}
          <div className="mb-8">
            <p className="text-gray-700 mb-4">
              This cancellation policy outlines how you can cancel or seek a refund for a product/service that you have purchased through MASHO Perfumes. At MASHO Perfumes, we strive to ensure your complete satisfaction with every purchase while maintaining fair business practices.
            </p>
            <p className="text-gray-700">
              Guidelines specific to certain brands may vary. Please review the applicable guidelines before making a purchase.
            </p>
          </div>

          {/* Order Cancellation */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaUndo className="mr-2 text-yellow-300" /> Order Cancellation
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <p className="text-gray-700 mb-2">
                <strong>Cancellation Timeline:</strong> Cancellations will only be considered if the request is made within <strong>12 hours</strong> of placing the order.
              </p>
            </div>
            
            <div className="ml-1">
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Cancellation requests may not be entertained if orders have been communicated to our fulfillment team and the shipping process has been initiated, or if the product is out for delivery.</li>
                <li>In such cases, you may choose to reject the product at the doorstep.</li>
                <li>MASHO Perfumes does not accept cancellation requests for customized items like custom-blended perfumes, personalized gifts, or special orders.</li>
                <li>However, refund/replacement can be made if you establish that the quality of the product delivered is not satisfactory.</li>
              </ul>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-300 p-4 mt-4">
                <p className="text-gray-700">
                  <strong>Note:</strong> MASHO Perfumes reserves the right to cancel any order due to pricing errors or stock unavailability.
                </p>
              </div>
            </div>
          </div>

          {/* Return & Exchange Timeline */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaCalendarAlt className="mr-2 text-yellow-300" /> Return Timeline
            </h2>
            <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
              <p className="text-gray-700">
                <strong>Return Window:</strong> We offer refund within the first <strong>7 days</strong> from the date of your purchase.
              </p>
            </div>
            
            <div className="ml-1">
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>If 7 days have passed since your purchase, you will not be offered a return, or refund of any kind.</li>
                <li>Disputes regarding damaged or defective items must be registered within <strong>3 days</strong> of receiving the product.</li>
                <li>After dispute approval, you must send the product back within 7 days.</li>
                <li>In case you feel that the product received is not as shown on the site or as per your expectations, you must bring it to the notice of our customer service within <strong>3 days</strong> of receiving the product.</li>
              </ul>
            </div>
          </div>

          {/* Damaged or Defective Items */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaExclamationTriangle className="mr-2 text-yellow-300" /> Damaged or Defective Items
            </h2>
            <p className="text-gray-700 mb-3">
              In case of receipt of damaged or defective items, please report to our customer service team immediately.
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>The request will be entertained once we have checked and determined the issue at our end.</li>
              <li>This should be reported within <strong>3 days</strong> of receipt of products.</li>
              <li>For faulty delivery complaints (e.g., stolen, damaged, or empty packages), an unboxing video is mandatory for your query to be entertained.</li>
              <li>Our customer service team will review your complaint and take appropriate action.</li>
            </ul>
          </div>

          {/* What Can Be Returned */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaCheckCircle className="mr-2 text-yellow-300" /> What Can Be Returned?
            </h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Faulty or defective products (e.g., damaged attar bottles, non-working burners)</li>
              <li>Products different from what you ordered</li>
              <li>Products that look different from their description</li>
              <li>Products that do not match the size you ordered</li>
              <li>If you are unsatisfied with the MASHO Perfumes range of attars</li>
            </ul>
          </div>

          {/* What Cannot Be Returned */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaTimesCircle className="mr-2 text-yellow-300" /> What Cannot Be Returned?
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>Items where the seal has been broken</li>
                  <li>Custom-made products (gifts, custom mixed perfumes)</li>
                  <li>Caps ordered in the wrong size</li>
                  <li>Products purchased during sales or discounts</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>Opened or used perfumes</li>
                  <li>Limited edition items (special policy applies)</li>
                  <li>Gift items with broken seals</li>
                  <li>Certain exempted categories (identified at purchase)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Warranty Items */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaBoxOpen className="mr-2 text-yellow-300" /> Warranty Items
            </h2>
            <p className="text-gray-700">
              In case of complaints regarding products that come with a warranty from the manufacturers, please refer the issue directly to them. We will assist you in connecting with the manufacturer's customer service team.
            </p>
          </div>

          {/* Conditions for Returning Products */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaInfoCircle className="mr-2 text-yellow-300" /> Conditions for Returning Products
            </h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Products must be unused and in the same condition as you received them</li>
              <li>Products must be returned in their original condition, along with bills, tags, and labels</li>
              <li>Items must have original packaging intact</li>
              <li>Products must be unsoiled and in their original form</li>
              <li>Brand packaging should remain intact</li>
              <li>Replacement availability depends on stock at the time of the return request</li>
            </ul>
          </div>

          {/* Return & Exchange Process */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaExchangeAlt className="mr-2 text-yellow-300" /> Return Process
            </h2>
            <ol className="list-decimal pl-6 text-gray-600 space-y-2">
              <li>To initiate a return for an eligible product, send an email to <span className="text-yellow-500">returns@mashoperfumes.com</span></li>
              <li>Include your order number, product details, and reason for return in your email</li>
              <li>Once your request is reviewed, you will receive instructions on how to return the item</li>
              <li>After we receive your returned product, it will be inspected</li>
              <li>We will send you an email to notify you about the receipt of your returned product</li>
              <li>If approved after quality check, your return will be processed according to our policies</li>
            </ol>
          </div>
          
          {/* Refund Process */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaCreditCard className="mr-2 text-yellow-300" /> Refund Process
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <p className="text-gray-700">
                <strong>Processing Time:</strong> In case of any refunds approved by MASHO Perfumes, it will take <strong>5-7 business days</strong> for the refund to be processed and credited to the particular bank account.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 p-4 rounded-lg flex">
                <div className="mr-3 text-yellow-300"><FaCalendarAlt size={24} /></div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Processing Timeline</h3>
                  <p className="text-gray-600 text-sm">All approved refunds processed within 5-7 business days</p>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg flex">
                <div className="mr-3 text-yellow-300"><FaCreditCard size={24} /></div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Refund Method</h3>
                  <p className="text-gray-600 text-sm">Refunds issued to original payment method</p>
                </div>
              </div>
            </div>
            <p className="text-gray-700">Depending on your payment provider, it may take an additional 3-10 business days for the refund amount to reflect in your account after our processing.</p>
          </div>
          
          {/* Return Shipping */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaTruck className="mr-2 text-yellow-300" /> Return Shipping
            </h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Damaged or Defective Products:</strong> MASHO Perfumes will cover the return shipping costs for damaged or defective items</li>
              <li><strong>Change of Mind:</strong> Customer is responsible for return shipping costs if returning due to change of mind</li>
              <li><strong>Return Instructions:</strong> Detailed return instructions will be provided once your return request is approved</li>
              <li><strong>Product Condition:</strong> All returns must be in their original packaging with all included accessories</li>
            </ul>
          </div>

          {/* Policy Updates */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaFileAlt className="mr-2 text-yellow-300" /> Changes to This Policy
            </h2>
            <p className="text-gray-700">
              We may update this Refund & Cancellation Policy periodically to reflect changes in our practices or for operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page with a new effective date.
            </p>
          </div>

          {/* Contact Us */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaEnvelope className="mr-2 text-yellow-300" /> Contact Us
            </h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Refund & Cancellation Policy or need to request a return or refund, please contact our customer service team:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded shadow-sm">
                <h3 className="font-medium text-gray-800 mb-2">Email</h3>
                <p className="text-yellow-500">returns@mashoperfumes.com</p>
              </div>
              <div className="bg-white p-4 rounded shadow-sm">
                <h3 className="font-medium text-gray-800 mb-2">WhatsApp - MOHAMMED MASHOOD</h3>
                <p className="text-gray-600">+91 70259 59481</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}